import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { settingsApi } from '../api/settingsApi';
import { useApi } from '../hooks/useApi';
import { DEFAULT_FLASHCARD_CONFIG } from '../../../shared/api';
import type { UpdateSettingsRequest, UserSettings } from '../../../shared/types';

const FALLBACK: UserSettings = {
  activeLanguage: 'es',
  // Only ever seen while the real settings load, or if that request fails.
  // The API applies its own fallback for accounts that predate the field, so a
  // loaded value is never empty.
  studyingLanguages: ['es'],
  flashcards: DEFAULT_FLASHCARD_CONFIG,
  sessionSize: 20,
};

/**
 * Repairs a settings payload from an API that predates a field.
 *
 * The client deploys straight to S3 while the API goes through the pipeline, so
 * there is always a window where a new client is talking to the previous
 * Lambda. `studyingLanguages` arriving as `undefined` would otherwise reach
 * `.map` on the home screen and blank the page — which is a poor trade for a
 * field with an obvious default.
 */
function normalize(loaded: UserSettings): UserSettings {
  const studying = loaded.studyingLanguages ?? [];
  return {
    ...loaded,
    studyingLanguages: studying.length > 0 ? studying : [loaded.activeLanguage],
  };
}

interface SettingsContextValue {
  settings: UserSettings;
  loading: boolean;
  /** Applies the change locally at once and saves it in the background. */
  update: (patch: UpdateSettingsRequest) => void;
  /** Set when the last save failed, so the UI can say the change is local only. */
  saveError: string | null;
}

const SettingsContext = createContext<SettingsContextValue | null>(null);

/** Milliseconds to wait after the last change before saving. */
const SAVE_DEBOUNCE_MS = 600;

/**
 * The learner's preferences, loaded once per session.
 *
 * Updates are optimistic and debounced. Dragging the speech-rate slider fires
 * dozens of changes, and the card must respond to every one of them while the
 * API hears about the last — so the state moves immediately and the save waits
 * for the dragging to stop.
 */
export function SettingsProvider({ children }: { children: ReactNode }) {
  const api = useApi();
  const [settings, setSettings] = useState<UserSettings>(FALLBACK);
  const [loading, setLoading] = useState(true);
  const [saveError, setSaveError] = useState<string | null>(null);

  const pending = useRef<UpdateSettingsRequest | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let alive = true;
    settingsApi
      .get(api)
      .then((loaded) => {
        if (alive) setSettings(normalize(loaded));
      })
      .catch(() => {
        // A failed settings read is not worth blocking study for — the defaults
        // are perfectly usable, and the next save will reconcile.
        if (alive) setSettings(FALLBACK);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [api]);

  const flush = useCallback(() => {
    const patch = pending.current;
    pending.current = null;
    if (!patch) return;
    settingsApi
      .update(patch, api)
      .then((saved) => {
        setSaveError(null);
        // Adopt the server's version: it clamps out-of-range numbers, so what
        // was sent is not always what was stored.
        setSettings(saved);
      })
      .catch((err: unknown) => {
        setSaveError(err instanceof Error ? err.message : 'Could not save your settings');
      });
  }, [api]);

  const update = useCallback(
    (patch: UpdateSettingsRequest) => {
      setSettings((prev) => ({
        ...prev,
        ...patch,
        flashcards: patch.flashcards
          ? { ...prev.flashcards, ...patch.flashcards, fields: { ...prev.flashcards.fields, ...patch.flashcards.fields } }
          : prev.flashcards,
      }));

      pending.current = pending.current
        ? {
            ...pending.current,
            ...patch,
            flashcards: patch.flashcards
              ? { ...pending.current.flashcards, ...patch.flashcards }
              : pending.current.flashcards,
          }
        : patch;

      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(flush, SAVE_DEBOUNCE_MS);
    },
    [flush],
  );

  // A change made a moment before navigating away must still be saved.
  useEffect(() => {
    return () => {
      if (timer.current) clearTimeout(timer.current);
      flush();
    };
  }, [flush]);

  const value = useMemo(
    () => ({ settings, loading, update, saveError }),
    [settings, loading, update, saveError],
  );

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings(): SettingsContextValue {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be used inside <SettingsProvider>');
  return ctx;
}
